<template>
  <Modal ref="modalRef" width="30rem">
    <template #header>Move to a portfolio</template>

    <template #body>
      <View direction="column" gap="md" class="form">
        <FormField label="Portfolio">
          <Select v-model="portfolioId">
            <option value="">No portfolio</option>
            <option v-for="portfolio in portfolios" :key="portfolio.id" :value="portfolio.id">
              {{ labelOf(portfolio) }}
            </option>
          </Select>
        </FormField>

        <Hint icon="info-circle">{{ hint }}</Hint>
      </View>
    </template>

    <template #footer>
      <Button variant="ghost" @click="close">Cancel</Button>
      <Button :loading="isLoading" :disabled="!hasChanged || isGone" @click="move">Move</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { Button, FormField, Hint, Modal, Select, View, useAlertsStore } from '@tallo/design-system';
import {
  PortfolioDto,
  teamMemberName as nameOf,
  useCompanyPortfoliosQuery,
  useCompanyTeamQuery,
} from '@tallo/investor/company';
import { escapeHtml } from '@tallo/utility';

import { TeamSubject } from '../../dtos/property-team.dto.interface';
import { useMovePropertyToPortfolio } from '../../mutations/property-team.mutation';
import { subjectNoun } from './team-roles';

const props = defineProps<{ subject: TeamSubject; currentPortfolio: { id: string; name: string } | null }>();

const alertsStore = useAlertsStore();
const modalRef = ref<InstanceType<typeof Modal>>();
const portfolioId = ref('');
const { portfolios } = useCompanyPortfoliosQuery();
const { members } = useCompanyTeamQuery();
const { mutateAsync, isLoading } = useMovePropertyToPortfolio();

const destination = computed(() => portfolios.value.find((portfolio) => portfolio.id === portfolioId.value) ?? null);
const hasChanged = computed(() => portfolioId.value !== (props.currentPortfolio?.id ?? ''));
// The chosen portfolio was deleted while the dialog was open; the select now shows nothing, so nothing is sent
const isGone = computed(() => Boolean(portfolioId.value) && !destination.value);
const hint = computed(() => {
  const keeps = `The ${subjectNoun(props.subject)} keeps its manager and collaborators.`;
  const target = destination.value;

  if (target) {
    const owner = members.value.find(({ investorId }) => investorId === target.ownerId);
    const runsIt = owner?.isYou ? 'you' : owner ? nameOf(owner) : 'its owner';

    return `${keeps} ${target.name}'s default collaborators join it, and ${runsIt} can reassign or move it from now on.`;
  }

  if (props.currentPortfolio && portfolioId.value === '') {
    return `${keeps} It leaves ${props.currentPortfolio.name}, so the portfolio's owner no longer runs it.`;
  }

  return keeps;
});

/** Two portfolios can share a name, so each is told apart by its owner */
function labelOf(portfolio: PortfolioDto): string {
  const owner = members.value.find(({ investorId }) => investorId === portfolio.ownerId);

  return owner ? `${portfolio.name} · ${owner.isYou ? 'you' : nameOf(owner)}` : portfolio.name;
}

function open() {
  portfolioId.value = props.currentPortfolio?.id ?? '';
  modalRef.value?.show();
}

function close() {
  modalRef.value?.close();
}

async function move() {
  const target = destination.value;
  const previous = props.currentPortfolio;

  try {
    await mutateAsync({ subject: props.subject, portfolioId: portfolioId.value || null });
  } catch {
    // The mutation already showed the server's message; the dialog stays open for another try
    return;
  }

  close();
  alertsStore.success(
    target
      ? `Moved to <b>${escapeHtml(target.name)}</b>`
      : `Taken out of <b>${escapeHtml(previous?.name ?? 'its portfolio')}</b>`,
  );
}

defineExpose({ open });
</script>

<style scoped lang="scss">
.form {
  width: 100%;
}
</style>
